import { create } from 'zustand';
import type { Blahblah, FeedType } from '@/types';
import * as blahblahsApi from '@/api/blahblahs';

interface FeedState {
  items: Blahblah[];
  feedType: FeedType;
  nextCursor: string | null;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  averageViews: number;

  // Actions
  loadFeed: () => Promise<void>;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  setFeedType: (type: FeedType) => void;
  likeBlahblah: (id: string) => Promise<void>;
  unlikeBlahblah: (id: string) => Promise<void>;
  favoriteBlahblah: (id: string) => Promise<void>;
  unfavoriteBlahblah: (id: string) => Promise<void>;
  deleteBlahblah: (id: string) => Promise<void>;
  updateBlahblah: (id: string, updates: Partial<Blahblah>) => void;
  addBlahblah: (blahblah: Blahblah) => void;
}

export const useFeedStore = create<FeedState>((set, get) => ({
  items: [],
  feedType: 'all',
  nextCursor: null,
  hasMore: true,
  isLoading: false,
  isLoadingMore: false,
  error: null,
  averageViews: 0,

  loadFeed: async () => {
    const { feedType } = get();
    set({ isLoading: true, error: null });

    try {
      const response = await blahblahsApi.getFeed(feedType);
      set({
        items: response.items,
        nextCursor: response.nextCursor,
        hasMore: response.hasMore,
        averageViews: response.stats?.averageViews || 0,
        isLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load feed';
      set({ error: message, isLoading: false });
    }
  },

  loadMore: async () => {
    const { feedType, nextCursor, hasMore, isLoadingMore } = get();
    if (!hasMore || isLoadingMore || !nextCursor) return;

    set({ isLoadingMore: true });

    try {
      const response = await blahblahsApi.getFeed(feedType, nextCursor);
      set((state) => ({
        items: [...state.items, ...response.items],
        nextCursor: response.nextCursor,
        hasMore: response.hasMore,
        isLoadingMore: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load more';
      set({ error: message, isLoadingMore: false });
    }
  },

  refresh: async () => {
    const { feedType } = get();
    set({ isLoading: true, error: null });

    try {
      const response = await blahblahsApi.getFeed(feedType);
      set({
        items: response.items,
        nextCursor: response.nextCursor,
        hasMore: response.hasMore,
        averageViews: response.stats?.averageViews || 0,
        isLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to refresh';
      set({ error: message, isLoading: false });
    }
  },

  setFeedType: (type) => {
    set({ feedType: type, items: [], nextCursor: null, hasMore: true });
    get().loadFeed();
  },

  likeBlahblah: async (id) => {
    // Optimistic update
    set((state) => ({
      items: state.items.map((item) =>
        item.id === id
          ? { ...item, isLiked: true, likesCount: item.likesCount + 1 }
          : item
      ),
    }));

    try {
      await blahblahsApi.likeBlahblah(id);
    } catch {
      // Revert on error
      set((state) => ({
        items: state.items.map((item) =>
          item.id === id
            ? { ...item, isLiked: false, likesCount: item.likesCount - 1 }
            : item
        ),
      }));
    }
  },

  unlikeBlahblah: async (id) => {
    // Optimistic update
    set((state) => ({
      items: state.items.map((item) =>
        item.id === id
          ? { ...item, isLiked: false, likesCount: item.likesCount - 1 }
          : item
      ),
    }));

    try {
      await blahblahsApi.unlikeBlahblah(id);
    } catch {
      // Revert on error
      set((state) => ({
        items: state.items.map((item) =>
          item.id === id
            ? { ...item, isLiked: true, likesCount: item.likesCount + 1 }
            : item
        ),
      }));
    }
  },

  favoriteBlahblah: async (id) => {
    // Optimistic update
    set((state) => ({
      items: state.items.map((item) =>
        item.id === id
          ? { ...item, isFavorited: true }
          : item
      ),
    }));

    try {
      await blahblahsApi.favoriteBlahblah(id);
    } catch {
      // Revert on error
      set((state) => ({
        items: state.items.map((item) =>
          item.id === id
            ? { ...item, isFavorited: false }
            : item
        ),
      }));
    }
  },

  unfavoriteBlahblah: async (id) => {
    // Optimistic update
    set((state) => ({
      items: state.items.map((item) =>
        item.id === id
          ? { ...item, isFavorited: false }
          : item
      ),
    }));

    try {
      await blahblahsApi.unfavoriteBlahblah(id);
    } catch {
      // Revert on error
      set((state) => ({
        items: state.items.map((item) =>
          item.id === id
            ? { ...item, isFavorited: true }
            : item
        ),
      }));
    }
  },

  deleteBlahblah: async (id) => {
    const { items } = get();
    const itemToDelete = items.find((item) => item.id === id);

    // Optimistic update
    set((state) => ({
      items: state.items.filter((item) => item.id !== id),
    }));

    try {
      await blahblahsApi.deleteBlahblah(id);
    } catch {
      // Revert on error
      if (itemToDelete) {
        set((state) => ({
          items: [...state.items, itemToDelete],
        }));
      }
    }
  },

  updateBlahblah: (id, updates) => {
    set((state) => ({
      items: state.items.map((item) =>
        item.id === id ? { ...item, ...updates } : item
      ),
    }));
  },

  addBlahblah: (blahblah) => {
    set((state) => ({
      items: [blahblah, ...state.items],
    }));
  },
}));
